import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Input,
  Space,
  message,
  Checkbox,
  Empty,
  Spin,
} from "antd";
import {
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import PincodeApi from "../../api/PincodeApi";
import "./CreateZoneModal.scss";

const CreateZoneModal = ({
  visible,
  onCancel,
  selectedState,
  selectedCity,
  onSuccess,
}) => {
  const [zoneName, setZoneName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pincodeData, setPincodeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPincodes, setSelectedPincodes] = useState([]);

  useEffect(() => {
    if (!visible) {
      // Reset form when modal closes
      setZoneName("");
      setSearchQuery("");
      setPincodeData([]);
      setSelectedPincodes([]);
    }
  }, [visible]);

  // Fetch pincode data automatically when modal opens
  useEffect(() => {
    const fetchPincodeData = async () => {
      if (visible && selectedState && selectedCity) {
        try {
          setLoading(true);
          const response = await PincodeApi.getPincodeAreaMaster(selectedState, selectedCity);
          
          if (response?.data?.items && Array.isArray(response.data.items)) {
            setPincodeData(response.data.items);
          } else {
            setPincodeData([]);
            message.info("No pincodes found for the selected city");
          }
        } catch (error) {
          console.error("Error fetching pincode area master:", error);
          message.error(error.message || "Failed to fetch pincode data. Please try again.");
          setPincodeData([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPincodeData();
  }, [visible, selectedState, selectedCity]);

  const handlePincodeToggle = (pincodeId) => {
    if (selectedPincodes.includes(pincodeId)) {
      // Remove pincode
      setSelectedPincodes(selectedPincodes.filter(id => id !== pincodeId));
    } else {
      // Add pincode (all areas will be included)
      setSelectedPincodes([...selectedPincodes, pincodeId]);
    }
  };


  const filteredPincodes = pincodeData.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.pincode?.toLowerCase().includes(query) ||
      item.area?.some(area => area.toLowerCase().includes(query))
    );
  });

  const handleCreate = () => {
    if (!zoneName.trim()) {
      message.warning("Please enter a zone name");
      return;
    }

    if (selectedPincodes.length === 0) {
      message.warning("Please select at least one pincode");
      return;
    }

    // Prepare serviceable pincodes data
    const serviceablePincodes = selectedPincodes.map(pincodeId => {
      const pincodeItem = pincodeData.find(item => item._id === pincodeId);
      return {
        pincode: pincodeItem.pincode,
        area: pincodeItem.area || [],
      };
    });

    // Call onSuccess with the zone data
    onSuccess({
      name: zoneName.trim(),
      state: selectedState,
      city: selectedCity,
      serviceablePincodes,
    });
  };

  return (
    <Modal
      title={
        <div className="create-zone-modal-header">
          <span className="modal-title">Create Zone</span>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            className="modal-close-btn"
          />
        </div>
      }
      visible={visible}
      footer={null}
      width={900}
      className="create-zone-modal"
      closeIcon={null}
      closable={false}
    >
      <div className="create-zone-content">
        {/* Zone Name Input */}
        <div className="form-section">
          <label className="form-label">Enter Zone Name *</label>
          <Input
            placeholder="Enter zone name"
            value={zoneName}
            onChange={(e) => setZoneName(e.target.value)}
            className="zone-name-input"
            size="large"
          />
        </div>

        {/* Search Area or Pincode */}
        <div className="form-section">
          <label className="form-label">Search Area or Pincode</label>
          <Input
            placeholder="Search by pincode or area name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            prefix={<SearchOutlined />}
            className="search-input"
            size="large"
          />
        </div>

        {/* Pincode List */}
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : filteredPincodes.length > 0 ? (
          <div className="pincode-list-section">
            <div className="pincode-grid">
              {filteredPincodes.map((item) => {
                const isPincodeSelected = selectedPincodes.includes(item._id);
                const areasToShow = item.area || [];
                const areasText = areasToShow.join(",");

                return (
                  <div 
                    key={item._id} 
                    className={`pincode-card ${isPincodeSelected ? "selected" : ""}`}
                  >
                    <Checkbox
                      checked={isPincodeSelected}
                      onChange={() => handlePincodeToggle(item._id)}
                      className="pincode-checkbox"
                    >
                      <span className="pincode-number">{item.pincode}</span>
                    </Checkbox>
                    <div className="pincode-areas">
                      {areasText || "No areas"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : pincodeData.length === 0 && searchQuery ? (
          <Empty
            description="No pincodes found. Try searching with different keywords."
            className="empty-state"
          />
        ) : null}
      </div>

      {/* Modal Footer */}
      <div className="modal-footer">
        <Space size={12}>
          <Button onClick={onCancel} className="close-btn-custom">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleCreate}
            className="create-btn-custom"
            disabled={!zoneName.trim() || selectedPincodes.length === 0}
          >
            Create Zone
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default CreateZoneModal;

